// The decide-rail panel for `retention-due` — "this document reached
// the end of its retention period; keep it, hold it, or destroy it?"
//
// Why a dedicated panel instead of a Cleanup disposition verb: the
// three answers are compliance actions on the DOCUMENT (they delete
// bytes, move a review date, set a legal hold), not dispositions of
// the cleanup issue. Routing them through /api/cleanup/decide would
// have meant teaching the generic decide path to destroy files —
// exactly the coupling that makes an irreversible action hard to
// reason about. The panel calls three purpose-built, admin-gated
// endpoints instead.
//
// DESIGN OF THE SCREEN ITSELF. The safe answers come first and are
// cheap: "Keep it longer" and "Put it on legal hold" each need one
// sentence. Destruction is last, collapsed behind its own toggle, and
// costs a reason code + a sentence + a typed phrase. That ordering is
// deliberate — when the dangerous answer is the easiest one to reach,
// people reach for it.
//
// Only system_admin and compliance (Grant and Baker) can see results
// from these endpoints; a non-admin who somehow reaches this panel
// gets an honest 403 message rather than a broken button.
//
// AFTER AN ACTION, the Cleanup issue itself clears on the next
// reconcile sweep rather than instantly: all three answers move the
// document out of the detector's WHERE (destroyed, held, or re-dated),
// and the reconciler auto-resolves an issue its detector no longer
// emits. Acting twice in the gap is harmless — a second destruction is
// refused as already-disposed, and a hold is idempotent — so this is a
// few hours of a stale row, never a double action.

import { useEffect, useState } from "react";

import { apiV11 } from "../api";
import { colors } from "../design/tokens";
import {
  DESTROY_CONFIRM_PHRASE,
  DISPOSITION_REASON_OPTIONS,
  defaultExtensionDate,
  destroyBlockedReason,
  destroyConsequence,
  reasonIsSufficient,
  retentionDocumentId,
  type RetentionIssueLike,
} from "./retention-due-issue";
import { DateField } from "../components/DateField";

const sectionLabel: React.CSSProperties = {
  fontFamily: "var(--heading-font-web)",
  fontWeight: 800,
  fontSize: 10.5,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "var(--blue-dark)",
};

const card: React.CSSProperties = {
  border: "1px solid var(--mid-gray)",
  borderRadius: 10,
  padding: "10px 12px",
  marginTop: 8,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  fontSize: 12.5,
  padding: "6px 8px",
  borderRadius: 6,
  border: "1px solid var(--mid-gray)",
  marginTop: 6,
};

const helpText: React.CSSProperties = {
  fontSize: 11.5,
  color: "var(--fg-muted)",
  lineHeight: 1.45,
  marginTop: 6,
};

const safeBtn: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 700,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--blue-dark)",
  background: "var(--blue)",
  color: "var(--white)",
  cursor: "pointer",
  marginTop: 8,
};

const dangerBtn: React.CSSProperties = {
  ...safeBtn,
  border: `1px solid ${colors.dangerHover}`,
  background: colors.danger,
};

const quietBtn: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  padding: "5px 10px",
  borderRadius: 6,
  border: "1px solid var(--mid-gray)",
  background: "transparent",
  color: "var(--fg-muted)",
  cursor: "pointer",
};

export interface RetentionDispositionPanelProps {
  issue: RetentionIssueLike & {
    affectedRecords: ReadonlyArray<{ kind: string; id: string; label?: string }>;
  };
  /** Called after a completed action so the rail can refresh the queue. */
  onResolved: (message: string) => void;
}

export function RetentionDispositionPanel({ issue, onResolved }: RetentionDispositionPanelProps) {
  const documentId = retentionDocumentId(issue);
  const filename =
    issue.affectedRecords.find((r) => r.kind === "activity")?.label?.replace(/^Document · /, "") ??
    null;
  const todayIso = new Date().toISOString().slice(0, 10);

  const [eligible, setEligible] = useState<boolean | null>(null);
  const [eligibilityMessage, setEligibilityMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [keepReason, setKeepReason] = useState("");
  const [keepDate, setKeepDate] = useState(() => defaultExtensionDate(todayIso));
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [destroyOpen, setDestroyOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [destroyReason, setDestroyReason] = useState("");
  const [typedConfirm, setTypedConfirm] = useState("");

  // Ask up front whether destruction is even permitted, so the panel
  // can say "this is on legal hold" BEFORE someone types a
  // confirmation phrase they were never going to be allowed to use.
  useEffect(() => {
    let cancelled = false;
    if (!documentId) {
      setEligible(false);
      setEligibilityMessage(
        "This issue doesn't point at exactly one document, so no action can be taken from here safely.",
      );
      return;
    }
    setEligible(null);
    setEligibilityMessage(null);
    apiV11
      .documentDisposalEligibility(documentId)
      .then((r) => {
        if (cancelled) return;
        setEligible(r.eligible);
        setEligibilityMessage(r.message);
      })
      .catch(() => {
        if (cancelled) return;
        setEligible(false);
        setEligibilityMessage(
          "Couldn't check whether this can be destroyed. Nothing has been changed.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  async function withBusy(fn: () => Promise<string>) {
    setBusy(true);
    setError(null);
    try {
      const message = await fn();
      setDone(message);
      onResolved(message);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't go through.");
    } finally {
      setBusy(false);
    }
  }

  if (!documentId) {
    return (
      <div style={card}>
        <div style={helpText}>{eligibilityMessage}</div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={card}>
        <div style={sectionLabel}>Done</div>
        <div style={helpText}>{done}</div>
      </div>
    );
  }

  const blocked = destroyBlockedReason({
    eligible,
    eligibilityMessage,
    reasonCode,
    reason: destroyReason,
    typedConfirm,
  });

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={sectionLabel}>Decide what happens to this document</div>

      {error && <div style={{ ...helpText, color: colors.danger, fontWeight: 600 }}>{error}</div>}

      {/* Answer 1 — keep it. The safe answer, and the cheapest to give. */}
      <div style={card}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>Keep it longer</div>
        <div style={helpText}>
          The retention schedule is a minimum, never a maximum — keeping a record past its date is
          always allowed. Say why, and pick when the OS should ask again.
        </div>
        <DateField
          value={keepDate}
          min={todayIso}
          onChange={setKeepDate}
          style={inputStyle}
          aria-label="Look at this again on"
        />
        <textarea
          value={keepReason}
          onChange={(e) => setKeepReason(e.target.value)}
          placeholder="Why is this being kept? The next reviewer reads this."
          rows={2}
          style={inputStyle}
        />
        <button
          type="button"
          style={{
            ...safeBtn,
            opacity: busy || !reasonIsSufficient(keepReason) ? 0.5 : 1,
          }}
          disabled={busy || !reasonIsSufficient(keepReason)}
          onClick={() =>
            withBusy(async () => {
              await apiV11.extendDocumentRetention(documentId, {
                newExpiresAt: keepDate,
                reason: keepReason.trim(),
              });
              return `Kept. The OS will ask again on ${keepDate}, with your reason attached.`;
            })
          }
        >
          Keep it — ask again later
        </button>
      </div>

      {/* Answer 2 — freeze it. */}
      <div style={card}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>Put it on legal hold</div>
        <div style={helpText}>
          For a document tied to a dispute, audit, or examination. A hold outranks the retention
          schedule entirely: while it is on, nothing can destroy this document — including a future
          reviewer who doesn&rsquo;t know the history.
        </div>
        {!holdOpen ? (
          <button
            type="button"
            style={{ ...quietBtn, marginTop: 8 }}
            onClick={() => setHoldOpen(true)}
          >
            Place a hold…
          </button>
        ) : (
          <>
            <textarea
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="Which matter is this hold for? A hold nobody can explain is a hold nobody will release."
              rows={2}
              style={inputStyle}
            />
            <button
              type="button"
              style={{
                ...safeBtn,
                opacity: busy || !reasonIsSufficient(holdReason) ? 0.5 : 1,
              }}
              disabled={busy || !reasonIsSufficient(holdReason)}
              onClick={() =>
                withBusy(async () => {
                  await apiV11.setDocumentLegalHold(documentId, {
                    hold: true,
                    reason: holdReason.trim(),
                  });
                  return "Legal hold placed. This document can't be destroyed until the hold is released.";
                })
              }
            >
              Place legal hold
            </button>
          </>
        )}
      </div>

      {/* Answer 3 — destroy it. Last, collapsed, and expensive on purpose. */}
      <div style={{ ...card, borderColor: colors.dangerBorder }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: colors.dangerHover }}>
          Destroy it permanently
        </div>
        <div style={helpText}>{destroyConsequence(filename)}</div>

        {eligible === false && (
          <div style={{ ...helpText, color: colors.dangerHover, fontWeight: 600 }}>
            {eligibilityMessage}
          </div>
        )}

        {eligible !== false &&
          (!destroyOpen ? (
            <button
              type="button"
              style={{ ...quietBtn, marginTop: 8 }}
              onClick={() => setDestroyOpen(true)}
            >
              I want to destroy this…
            </button>
          ) : (
            <>
              <div style={{ ...helpText, fontWeight: 600, marginTop: 10 }}>
                Reason for destroying
              </div>
              {DISPOSITION_REASON_OPTIONS.map((opt) => (
                <label
                  key={opt.code}
                  style={{
                    display: "block",
                    fontSize: 12,
                    marginTop: 6,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="disposition-reason"
                    checked={reasonCode === opt.code}
                    onChange={() => setReasonCode(opt.code)}
                    style={{ marginRight: 6 }}
                  />
                  {opt.label}
                  <div style={{ ...helpText, marginTop: 2, marginLeft: 20 }}>{opt.when}</div>
                </label>
              ))}

              <textarea
                value={destroyReason}
                onChange={(e) => setDestroyReason(e.target.value)}
                placeholder="In your own words: why is this being destroyed?"
                rows={2}
                style={inputStyle}
              />

              <div style={{ ...helpText, marginTop: 10 }}>
                Type <strong>{DESTROY_CONFIRM_PHRASE}</strong> to confirm.
              </div>
              <input
                type="text"
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                placeholder={DESTROY_CONFIRM_PHRASE}
                style={inputStyle}
                aria-label={`Type ${DESTROY_CONFIRM_PHRASE} to confirm`}
              />

              <button
                type="button"
                style={{ ...dangerBtn, opacity: busy || blocked ? 0.5 : 1 }}
                disabled={busy || blocked !== null}
                onClick={() =>
                  withBusy(async () => {
                    const r = await apiV11.destroyDocument(documentId, {
                      confirm: typedConfirm.trim(),
                      reasonCode,
                      reason: destroyReason.trim(),
                    });
                    return r.message;
                  })
                }
              >
                Destroy permanently
              </button>
              {blocked && <div style={helpText}>{blocked}</div>}
            </>
          ))}
      </div>
    </div>
  );
}
